import React, { useMemo, useRef, useState, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Text } from "@react-three/drei";

/**
 * 1. The Fabric Strap
 */
function Strap({ color = "#d2afa8" }) {
  const curve = useMemo(() => {
    const radius = 0.84;
    const points = [];
    const segments = 12;
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
    }
    const curvePath = new THREE.CatmullRomCurve3(points);
    curvePath.closed = true;
    curvePath.tension = 0.5;
    return curvePath;
  }, []);

  const shape = useMemo(() => {
    const s = new THREE.Shape();
    const width = 0.4;
    const thick = 0.08;
    s.moveTo(-width / 2, -thick / 2);
    s.lineTo(width / 2, -thick / 2);
    s.lineTo(width / 2, thick / 2);
    s.lineTo(-width / 2, thick / 2);
    s.lineTo(-width / 2, -thick / 2);
    return s;
  }, []);

  const extrudeSettings = useMemo(() => ({ steps: 64, curveSegments: 16, extrudePath: curve }), [curve]);
  const strapColor = useMemo(() => new THREE.Color(color), [color]);

  return (
    <mesh castShadow receiveShadow>
      <extrudeGeometry args={[shape, extrudeSettings]} />
      <meshStandardMaterial color={strapColor} roughness={0.8} metalness={0.1} />
    </mesh>
  );
}

/**
 * 2. The Metal Hardware (Clasp)
 */
function Hardware() {
  const CLASP_COLOR = "#fff"; 
  const [time, setTime] = useState("");
  const [day, setDay] = useState("");
  const dummyTemp = "24°C"; 

  useEffect(() => {
    const updateTimeDisplay = () => {
      const now = new Date();
      setDay(now.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase().replace('.', ''));
      const hours = now.getHours().toString().padStart(2, '0'); 
      const minutes = now.getMinutes().toString().padStart(2, '0');
      setTime(`${hours}\n${minutes}`); 
    };
    updateTimeDisplay(); 
    const intervalId = setInterval(updateTimeDisplay, 1000); 
    return () => clearInterval(intervalId); 
  }, []);

  const xPos = -0.041; 
  const textRotation = [0, -Math.PI / 2, -Math.PI / 2];

  return (
    <group position={[-0.84, 0, 0]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[0.08, 0.42, 0.8]} />
        <meshStandardMaterial color={CLASP_COLOR} metalness={0.9} roughness={0.2} />
      </mesh>
      <Text
        position={[xPos, 0, 0]}
        rotation={textRotation}
        fontSize={1.2}
        lineHeight={1}
        font="/fonts/Anton-Regular.ttf" 
        scale={[0.18, 0.18, 0.18]} 
        textAlign="center"
        material={new THREE.MeshBasicMaterial({ color: "#666" })} 
        anchorX="center"
        anchorY="middle"
      >
        {time}
      </Text>
      <Text
        position={[xPos, 0, -0.3]} 
        rotation={textRotation} 
        fontSize={0.3} 
        font="/fonts/Poppins-Light.ttf" 
        scale={[0.18, 0.18, 0.18]} 
        textAlign="center"
        material={new THREE.MeshBasicMaterial({ color: "white" })} 
        anchorX="center"
        anchorY="middle"
      >
        {day}
      </Text>
      <Text
        position={[xPos, 0, 0.3]} 
        rotation={textRotation} 
        fontSize={0.3} 
        font="/fonts/Poppins-Light.ttf" 
        scale={[0.18, 0.18, 0.18]} 
        textAlign="center"
        material={new THREE.MeshBasicMaterial({ color: "white" })} 
        anchorX="center"
        anchorY="middle"
      >
        {dummyTemp}
      </Text>
      <mesh position={[0, 0.21, -0.05]} rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.6, 16]} />
        <meshStandardMaterial color={CLASP_COLOR} metalness={0.9} roughness={0.2} />
      </mesh>
      <mesh position={[0, -0.21, -0.05]} rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.6, 16]} />
        <meshStandardMaterial color={CLASP_COLOR} metalness={0.9} roughness={0.2} />
      </mesh>
    </group>
  );
}

/**
 * 3. The Sensor Unit
 */
function Sensor() {
  return (
    <group position={[0.84, -0.05, 0]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[0.06, 0.4, 0.40]} />
        <meshStandardMaterial color="#666" roughness={0.4} metalness={0.1} />
      </mesh>
      <group position={[-0.035, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
        <mesh position={[0, 0.15, 0]}>
          <circleGeometry args={[0.04, 32]} />
          <meshBasicMaterial color="#00ff00" />
        </mesh>
        <mesh position={[0, -0.15, 0]}>
          <circleGeometry args={[0.04, 32]} />
          <meshBasicMaterial color="#00ff00" />
        </mesh>
      </group>
    </group>
  );
}

/**
 * Main FitnessBandModel Component with Fly-in Animation
 */
export function FitnessBandModel({ scroll }) {
  const groupRef = useRef();
  const [isEntryStarted, setIsEntryStarted] = useState(false);
  const entryProgress = useRef(0);

  useEffect(() => {
    // Wait for the slogan stagger animation (~2 seconds)
    const timer = setTimeout(() => {
      setIsEntryStarted(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current || !isEntryStarted) return;

    // 1. Advance linear progress
    if (entryProgress.current < 1) {
      entryProgress.current += delta * 1.0; // Slightly slowed for a more elegant feel
      if (entryProgress.current > 1) entryProgress.current = 1;
    }

    // 2. Apply Easing (Cubic Out: 1 - (1 - x)^3) 
    // This makes the animation slow down smoothly at the end.
    const p = 1 - Math.pow(1 - entryProgress.current, 3);

    // Position: From right (5) to center (0) using eased progress
    const currentX = THREE.MathUtils.lerp(5, 0, p);
    groupRef.current.position.set(currentX, 0.2, 0);

    // Rotation logic
    const baseRotationX = -1.56;
    const baseRotationY = 0.4;
    const baseRotationZ = 1.2;

    // 3 Full rotations (6 * PI) during entry, reducing to 0 at p=1
    const entrySpin = THREE.MathUtils.lerp(Math.PI * 2, 0, p);
    
    // Add scroll-based rotation
    const scrollSpin = scroll * Math.PI * 4;

    groupRef.current.rotation.x = baseRotationX;
    groupRef.current.rotation.y = baseRotationY;
    groupRef.current.rotation.z = baseRotationZ + entrySpin + scrollSpin;
    
    // Ensure visibility
    groupRef.current.visible = true;
  });

  return (
    <group
      ref={groupRef}
      scale={[1.0, 1.0, 1.0]}
      position={[5, 0.2, 0]} // Initial off-screen position
      visible={false}        // Hidden until timer starts
    >
      <Strap />
      <Hardware />
      <Sensor />
    </group>
  );
}